"use client";

import { useState, useEffect } from "react";
import { Check, CircleX, Copy } from "lucide-react";
import { Button, type ButtonProps } from "./buttons/button";
import { Typography } from "./typography";
import { cn } from "@drpcrd/utils/shared";
import { removeHttpProtocol } from "@drpcrd/utils/formatters";

type CopyTextButtonProps = Omit<ButtonProps, "onClick" | "children"> & {
  placeholder?: string;
  textToCopy: string;
  copiedText?: string;
  errorText?: string;
};

/**
 * A button that copies a given text to the clipboard and provides visual feedback.
 */
export const CopyTextButton = ({
  placeholder,
  textToCopy,
  copiedText = "Link copied!",
  errorText = "Link failed to copy",
  className,
}: CopyTextButtonProps) => {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  useEffect(() => {
    if (copyState === "idle") {
      return;
    }

    const timer = setTimeout(() => {
      setCopyState("idle");
    }, 1000);

    return () => clearTimeout(timer);
  }, [copyState]);

  const onCopy = async () => {
    if (copyState !== "idle") {
      return;
    }
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  return (
    <Button
      type="button"
      variant="filled"
      onClick={onCopy}
      className={cn(
        "py-0.75 border-0! flex w-full items-center justify-center gap-1 rounded-md px-1.5 text-white transition-colors",
        copyState === "copied" && "bg-green-800 hover:bg-green-900",
        copyState === "error" && "bg-pink-700 hover:bg-pink-800",
        copyState === "idle" && "bg-green-700 hover:bg-green-800",
        className,
      )}
    >
      {/* Button label */}
      <Typography
        size="sm"
        color="white"
        weight="semibold"
        className="flex-1 truncate"
      >
        {copyState === "idle"
          ? (placeholder ?? removeHttpProtocol(textToCopy))
          : copyState === "copied"
            ? copiedText
            : errorText}
      </Typography>

      {/* Right Icon */}
      {copyState === "idle" ? (
        <Copy className="size-2" />
      ) : copyState === "copied" ? (
        <Check className="size-2" />
      ) : (
        <CircleX className="size-2" />
      )}
    </Button>
  );
};
